import React, { useEffect, useState } from "react";
import FicheHeader from "./FicheHeader";
import { SUPPLIER_CATEGORIES, SUPPLIER_CATEGORY_LABELS } from "./SupplierPicker";
import { api, formatEURDecimal, formatDateFR, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Loader2, FileText, X, ExternalLink, Archive, ArchiveRestore, Trash2, Wrench } from "lucide-react";
import { confirmAction } from "../ConfirmDialog";

const inputCls = "w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-violet-600 outline-none";
const labelCls = "text-[10px] tracking-[0.15em] uppercase font-bold text-slate-500";

const FIELDS = [
  ["name", "Raison sociale *"], ["trade_name", "Nom commercial"], ["siret", "SIRET"], ["vat_number", "N° TVA"],
  ["address", "Adresse"], ["phone", "Téléphone"], ["email", "Email"], ["website", "Site web"],
  ["contact_name", "Contact principal"],
];

// ─── Achats liés (ACH- · Facturation Phase 2) ────────────────────────────────
function SupplierPurchases({ supplierId }) {
  const [purchases, setPurchases] = useState(null);
  useEffect(() => {
    api.get(`/vendor/purchases?supplier_id=${supplierId}`)
      .then((r) => setPurchases(r.data.purchases || []))
      .catch(() => setPurchases([]));
  }, [supplierId]);

  if (purchases === null) return null;
  const total = purchases.reduce((s, p) => s + (p.amount_ttc || 0), 0);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6" data-testid="supplier-purchases">
      <div className="overline mb-4">Achats ({purchases.length}){total > 0 ? ` · ${formatEURDecimal(total)}` : ""}</div>
      {purchases.length === 0 ? (
        <div className="text-sm text-slate-400">Aucun achat. Enregistrez-les depuis Revenus → Achats fournisseurs.</div>
      ) : (
        <div className="space-y-2">
          {purchases.map((p) => (
            <div key={p.id} className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl border border-slate-200" data-testid={`supplier-purchase-${p.reference}`}>
              <div className="min-w-0">
                <div className="text-xs font-semibold text-slate-900 truncate">{p.reference} · {p.label}</div>
                <div className="text-[10px] text-slate-500">{formatDateFR(p.date)}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${p.status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                  {p.status === "paid" ? "Payé" : "À payer"}
                </span>
                <span className="text-sm font-semibold text-slate-900">{formatEURDecimal(p.amount_ttc)}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function SupplierFiche({ supplierId, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [acting, setActing] = useState(false);

  const load = () => {
    api.get(`/vendor/suppliers/${supplierId}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setLoading(true); load(); }, [supplierId]);

  const patch = async (payload, msg) => {
    setActing(true);
    try {
      await api.patch(`/vendor/suppliers/${supplierId}`, payload);
      if (msg) toast.success(msg);
      load();
      return true;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erreur");
      return false;
    } finally {
      setActing(false);
    }
  };

  const startEdit = () => {
    const s = data.supplier;
    setForm(Object.fromEntries([...FIELDS.map(([k]) => [k, s[k] || ""]), ["category", s.category || "autre"], ["notes", s.notes || ""]]));
    setEditing(true);
  };
  const save = async () => { if (await patch(form, "Fournisseur mis à jour")) setEditing(false); };

  const remove = async () => {
    if (!(await confirmAction({
      title: "Supprimer définitivement ce fournisseur ?",
      message: "La fiche fournisseur sera définitivement supprimée. Cette action est IRRÉVERSIBLE.",
      confirmLabel: "Supprimer le fournisseur",
      variant: "danger",
      requireText: "SUPPRIMER",
    }))) return;
    try {
      await api.delete(`/vendor/suppliers/${supplierId}`);
      toast.success("Fournisseur supprimé");
      onBack();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erreur");
    }
  };

  const uploadDoc = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/vendor/suppliers/${supplierId}/document`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document ajouté");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erreur");
    }
  };
  const deleteDoc = async (path) => {
    await api.delete(`/vendor/suppliers/${supplierId}/document?path=${encodeURIComponent(path)}`).catch(() => {});
    load();
  };
  const openDoc = async (path) => {
    try {
      const res = await api.get(`/files/${path}`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch { toast.error("Impossible d'ouvrir le document"); }
  };

  if (loading) return <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-violet-600" /></div>;
  if (!data) return <div className="py-16 text-center text-sm text-slate-500">Fournisseur introuvable.</div>;

  const s = data.supplier;
  const stats = data.stats || {};
  const interventions = data.interventions || [];
  const archived = !!s.archived;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6" data-testid="supplier-fiche">
      <FicheHeader
        onBack={onBack}
        backLabel="Retour aux fournisseurs"
        title={s.trade_name || s.name}
        subtitle={`${s.reference} · ${SUPPLIER_CATEGORY_LABELS[s.category] || s.category}`}
        badge={archived ? <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-200 text-slate-600" data-testid="supplier-archived-badge">Archivé</span>
          : <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700">Actif</span>}
        editing={editing}
        onEdit={archived ? undefined : startEdit}
        onCancel={() => setEditing(false)}
        onSave={save}
        saving={acting}
        actions={!editing && (
          <>
            {archived ? (
              <button onClick={() => patch({ archived: false }, "Fournisseur restauré")} disabled={acting}
                className="h-10 px-4 rounded-xl border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 inline-flex items-center gap-2" data-testid="supplier-unarchive-btn">
                <ArchiveRestore className="h-3.5 w-3.5" /> Restaurer
              </button>
            ) : (
              <button onClick={() => patch({ archived: true }, "Fournisseur archivé")} disabled={acting}
                className="h-10 px-4 rounded-xl border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 inline-flex items-center gap-2" data-testid="supplier-archive-btn">
                <Archive className="h-3.5 w-3.5" /> Archiver
              </button>
            )}
            <button onClick={remove} className="h-10 px-4 rounded-xl border border-red-200 text-sm font-medium text-red-600 hover:bg-red-50 inline-flex items-center gap-2" data-testid="supplier-delete-btn">
              <Trash2 className="h-3.5 w-3.5" /> Supprimer
            </button>
          </>
        )}
        testid="supplier-fiche-header"
      />

      <div className="grid lg:grid-cols-3 gap-5 mt-6">
        <div className="lg:col-span-2 space-y-5">
          {/* Coordonnées */}
          <div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6" data-testid="supplier-details">
            <div className="overline mb-4">Coordonnées</div>
            {editing ? (
              <div className="grid sm:grid-cols-2 gap-3">
                {FIELDS.map(([k, l]) => (
                  <div key={k} className={k === "address" ? "sm:col-span-2" : ""}>
                    <label className={labelCls}>{l}</label>
                    <input value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className={inputCls + " mt-1"} data-testid={`supplier-edit-${k}`} />
                  </div>
                ))}
                <div>
                  <label className={labelCls}>Catégorie</label>
                  <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputCls + " mt-1"} data-testid="supplier-edit-category">
                    {SUPPLIER_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className={labelCls}>Commentaires</label>
                  <textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={inputCls + " mt-1 resize-none"} data-testid="supplier-edit-notes" />
                </div>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                {FIELDS.map(([k, l]) => (
                  <div key={k} className="flex justify-between gap-3">
                    <span className="text-slate-500">{l.replace(" *", "")}</span>
                    <span className="font-medium text-slate-900 text-right truncate">{s[k] || "—"}</span>
                  </div>
                ))}
                {s.notes && <p className="sm:col-span-2 text-slate-600 pt-2 border-t border-slate-100 whitespace-pre-wrap">{s.notes}</p>}
              </div>
            )}
          </div>

          {/* Historique interventions (lecture seule) */}
          <div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6" data-testid="supplier-history">
            <div className="overline mb-4">Interventions liées ({stats.interventions_count || 0})</div>
            {interventions.length === 0 ? (
              <div className="text-sm text-slate-400">Aucune intervention liée. Sélectionnez ce fournisseur lors de la création d'une intervention.</div>
            ) : (
              <div className="space-y-2">
                {interventions.map((r) => (
                  <div key={r.id} className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl border border-slate-200" data-testid={`supplier-intervention-${r.id}`}>
                    <div className="flex items-center gap-2 min-w-0">
                      <Wrench className="h-4 w-4 text-violet-600 shrink-0" />
                      <div className="min-w-0">
                        <div className="text-xs font-semibold text-slate-900 truncate">{r.reference ? `${r.reference} · ` : ""}{r.title}</div>
                        <div className="text-[10px] text-slate-500">{r.vehicle_name} · {formatDateFR(r.date)}</div>
                      </div>
                    </div>
                    <span className="text-sm font-semibold text-slate-900 shrink-0">{r.cost != null ? formatEURDecimal(r.cost) : "—"}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          {/* Achats fournisseur (ACH-) */}
          <SupplierPurchases supplierId={supplierId} />
        </div>

        <div className="space-y-4">
          {/* Stats */}
          <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="supplier-stats">
            <div className="overline mb-3">Historique</div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Coût total</span><span className="font-semibold text-slate-900" data-testid="supplier-total-cost">{formatEURDecimal(stats.total_cost || 0)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Interventions</span><span className="font-semibold text-slate-900">{stats.interventions_count || 0}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Dernier passage</span><span className="font-medium text-slate-900">{stats.last_date ? formatDateFR(stats.last_date) : "—"}</span></div>
            </div>
            {Object.keys(stats.per_vehicle || {}).length > 0 && (
              <div className="mt-3 pt-3 border-t border-slate-100">
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-2">Dépenses par véhicule</div>
                <div className="space-y-1.5 text-xs">
                  {Object.entries(stats.per_vehicle).map(([v, c]) => (
                    <div key={v} className="flex justify-between"><span className="text-slate-600 truncate">{v}</span><span className="font-semibold shrink-0">{formatEURDecimal(c)}</span></div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Documents généraux */}
          <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="supplier-documents">
            <div className="flex items-center justify-between mb-3">
              <div className="overline">Documents ({(s.documents || []).length})</div>
              <label className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-slate-200 text-xs font-semibold text-slate-700 hover:bg-slate-50 cursor-pointer" data-testid="supplier-add-document-btn">
                <FileText className="h-3.5 w-3.5" /> Ajouter
                <input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => { if (e.target.files?.[0]) uploadDoc(e.target.files[0]); e.target.value = ""; }} />
              </label>
            </div>
            {(s.documents || []).length === 0 ? (
              <div className="text-xs text-slate-400">Contrat cadre, garantie, RIB… Les factures d'interventions restent sur chaque intervention.</div>
            ) : (
              <div className="space-y-2">
                {s.documents.map((d, idx) => (
                  <div key={idx} className="flex items-center justify-between gap-2 px-3 py-2 rounded-xl border border-slate-200" data-testid={`supplier-document-${idx}`}>
                    <button onClick={() => openDoc(d.path)} className="flex items-center gap-2 min-w-0 text-sm text-slate-700 hover:text-violet-700">
                      <FileText className="h-4 w-4 text-violet-600 shrink-0" />
                      <span className="truncate text-xs">{d.name || "Document"}</span>
                      <ExternalLink className="h-3 w-3 text-slate-400 shrink-0" />
                    </button>
                    <button onClick={() => deleteDoc(d.path)} className="h-6 w-6 rounded-md hover:bg-red-50 text-red-500 inline-flex items-center justify-center shrink-0" data-testid={`supplier-document-delete-${idx}`}>
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
